import { useEffect, useState } from "react";
import api, { dkk, num } from "@/lib/api";
import { ResponsiveContainer, LineChart, Line, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Layers } from "lucide-react";

const COLORS = ["#38BDF8", "#FB923C", "#34D399", "#A78BFA", "#F472B6"];

const MONTHS_DA = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
function fmtPeriod(p) {
  if (!p || typeof p !== "string") return p;
  if (p.includes("-U")) return "U" + p.split("-U")[1];
  const parts = p.split("-");
  if (parts.length === 3) return `${parseInt(parts[2], 10)}/${parseInt(parts[1], 10)}`;
  if (parts.length === 2) return MONTHS_DA[parseInt(parts[1], 10) - 1] || p;
  return p;
}

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-3 py-2 text-xs">
      <p className="text-slate-500 mb-1">{fmtPeriod(label)}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-500">{p.name}</span>
          <span className="ml-auto font-medium text-slate-800">{dkk(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export function CategoryStats({ store, dateParams }) {
  const [data, setData] = useState({ totals: [], trend_categories: [], trend: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dateParams) return;
    let active = true;
    setLoading(true);
    api.get("/kpi/category-stats", { params: { ...dateParams, store, granularity: dateParams.gran || "day" } })
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => { if (active) setData({ totals: [], trend_categories: [], trend: [] }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [store, dateParams]);

  const { totals, trend_categories, trend } = data;
  const totRev = totals.reduce((s, r) => s + (r.revenue || 0), 0);
  const totUnits = totals.reduce((s, r) => s + (r.units || 0), 0);
  const totProfit = totals.reduce((s, r) => s + (r.profit || 0), 0);

  if (loading) {
    return (
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="category-stats">
        <p className="text-slate-500 text-sm py-8 text-center">Henter…</p>
      </div>
    );
  }

  if (!totals.length) {
    return (
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="category-stats">
        <p className="text-slate-500 text-sm py-8 text-center" data-testid="category-stats-empty">
          Ingen synkroniserede varedata for perioden endnu.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="category-stats">
      {/* Time series */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="category-trend">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 flex items-center gap-2 mb-3">
          <Layers className="w-4 h-4" /> Omsætning pr. kategori over tid
        </h3>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mb-2">
          {trend_categories.map((c, i) => (
            <span key={c} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{c}
            </span>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={trend} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} minTickGap={22} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={false} width={52} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
            <Tooltip content={<TrendTooltip />} />
            {trend_categories.map((c, i) => (
              <Line key={c} type="monotone" dataKey={c} name={c} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={trend.length < 2} />
            ))}
          </LineChart>
        </ResponsiveContainer>
        {trend.length < 2 && (
          <p className="text-xs text-slate-500 mt-2" data-testid="category-trend-hint">Ikke nok data i perioden til at vise en udvikling — vælg et større interval.</p>
        )}
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">Kategori-statistik</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]" data-testid="category-stats-table">
            <thead>
              <tr className="text-slate-500 text-xs uppercase tracking-wider border-b border-[#e2e8f0]">
                <th className="text-left font-medium py-2 pr-2 w-8">#</th>
                <th className="text-left font-medium py-2 pr-2">Kategori</th>
                <th className="text-right font-medium py-2 pr-2">Antal solgt</th>
                <th className="text-right font-medium py-2 pr-2">Omsætning</th>
                <th className="text-right font-medium py-2 pr-2">Fortjeneste</th>
                <th className="text-right font-medium py-2">Margin</th>
              </tr>
            </thead>
            <tbody>
              {totals.map((r, i) => {
                const revEx = r.revenue / 1.25;
                const margin = revEx ? Math.round((r.profit / revEx) * 100) : 0;
                return (
                  <tr key={r.category + i} className="border-b border-[#e2e8f0] hover:bg-[#f8fafc]/60" data-testid={`category-row-${i}`}>
                    <td className="py-2 pr-2 text-slate-500 tabular-nums">{i + 1}</td>
                    <td className="py-2 pr-2 text-slate-700">{r.category}</td>
                    <td className="py-2 pr-2 text-right tabular-nums text-slate-800">{num(r.units)}</td>
                    <td className="py-2 pr-2 text-right tabular-nums text-slate-800">{dkk(r.revenue)}</td>
                    <td className="py-2 pr-2 text-right tabular-nums" style={{ color: (r.profit || 0) < 0 ? "#F87171" : "#34D399" }}>{dkk(r.profit)}</td>
                    <td className="py-2 text-right tabular-nums text-slate-500">{margin}%</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="text-slate-500 font-medium border-t border-[#e2e8f0]">
                <td></td>
                <td className="py-2.5">{totals.length} kategorier</td>
                <td className="py-2.5 text-right tabular-nums">{num(totUnits)}</td>
                <td className="py-2.5 text-right tabular-nums">{dkk(totRev)}</td>
                <td className="py-2.5 text-right tabular-nums" style={{ color: totProfit < 0 ? "#F87171" : "#34D399" }}>{dkk(totProfit)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
